import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDomains } from '../hooks/useDomains';
import supabaseService from '../services/supabaseService';

const ModernDomainsPage = () => {
  const { user } = useAuth();
  const { domains, loading, createDomain, verifyDomain, deleteDomain } = useDomains();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newDomain, setNewDomain] = useState('');
  const [verifying, setVerifying] = useState(null);

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.trim()) return;

    try {
      await createDomain({
        domain: newDomain.trim(),
        redirectType: 'TEMPORARY'
      });
      setNewDomain('');
      setShowAddModal(false);
    } catch (error) {
      alert('Failed to add domain: ' + error.message);
    }
  };

  const handleVerifyDomain = async (domain) => {
    try {
      setVerifying(domain.id);
      await verifyDomain(domain);
    } catch (error) {
      alert('Failed to verify domain: ' + error.message);
    } finally {
      setVerifying(null);
    }
  };

  const handleCheckSSL = async (domain) => {
    try {
      setVerifying(domain.id);
      console.log('🔒 Checking SSL for domain:', domain.domain);

      // Check SSL status
      const sslData = await supabaseService.checkSSLStatus(domain.domain);

      // Update domain with SSL status
      await supabaseService.updateDomainSSL(domain.id, sslData);

      // Refresh domains to show updated status
      window.location.reload();

    } catch (error) {
      alert('Failed to check SSL: ' + error.message);
    } finally {
      setVerifying(null);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (window.confirm('Are you sure you want to delete this domain? This action cannot be undone.')) {
      try {
        await deleteDomain(domainId);
      } catch (error) {
        alert('Failed to delete domain: ' + error.message);
      }
    }
  };

  const handleUpdateDefaultDestination = async (domainId, destinationUrl) => {
    try {
      // Debounce the update to avoid too many API calls
      clearTimeout(window.destinationUpdateTimeout);
      window.destinationUpdateTimeout = setTimeout(async () => {
        console.log('🎯 Updating default destination for domain:', domainId, 'to:', destinationUrl);

        // Update domain with new default destination
        await supabaseService.updateDomain(domainId, {
          default_destination_url: destinationUrl || null
        });

        console.log('✅ Default destination updated successfully');
      }, 1000);
    } catch (error) {
      console.error('❌ Failed to update default destination:', error);
      alert('Failed to update default destination: ' + error.message);
    }
  };

  return (
    <div className="modern-domains">
      {/* Header */}
      <div className="domains-hero">
        <div className="hero-content">
          <div className="title-section">
            <h1>🌐 Domain Management</h1>
            <p>Manage your custom domains for branded short links</p>
          </div>

          <button
            className="add-domain-btn"
            onClick={() => setShowAddModal(true)}
          >
            <span className="btn-icon">➕</span>
            Add Domain
          </button>
        </div>
      </div>

      {/* Domains List */}
      <div className="domains-section">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <h3>Loading domains...</h3>
          </div>
        ) : domains.length > 0 ? (
          <div className="domains-grid">
            {domains.map(domain => (
              <div key={domain.id} className="domain-card">
                <div className="domain-header">
                  <div className="domain-info">
                    <h3 className="domain-name">{domain.domain}</h3>
                    <div className="domain-badges">
                      <span className={`status-badge ${domain.isVerified ? 'verified' : 'pending'}`}>
                        {domain.isVerified ? '✅ Verified' : '⏳ Pending'}
                      </span>
                      <span className={`ssl-badge ${domain.sslStatus || 'pending'}`}>
                        {domain.sslEnabled !== false ? (
                          domain.sslStatus === 'issued' ? '🔒 SSL Active' :
                          domain.sslStatus === 'pending' ? '⏳ SSL Pending' :
                          domain.sslStatus === 'failed' ? '❌ SSL Failed' :
                          '🔄 SSL Processing'
                        ) : (
                          '🔓 SSL Disabled'
                        )}
                      </span>
                      <span className="type-badge">
                        {domain.redirectType || 'TEMPORARY'}
                      </span>
                    </div>
                  </div>

                  <div className="domain-actions">
                    {!domain.isVerified && (
                      <button
                        className="verify-btn"
                        onClick={() => handleVerifyDomain(domain)}
                        disabled={verifying === domain.id}
                      >
                        {verifying === domain.id ? (
                          <>
                            <div className="btn-spinner"></div>
                            Verifying...
                          </>
                        ) : (
                          '🔍 Verify'
                        )}
                      </button>
                    )}

                    {domain.isVerified && domain.sslStatus !== 'issued' && (
                      <button
                        className="ssl-check-btn"
                        onClick={() => handleCheckSSL(domain)}
                        disabled={verifying === domain.id}
                      >
                        {verifying === domain.id ? (
                          <>
                            <div className="btn-spinner"></div>
                            Checking...
                          </>
                        ) : (
                          '🔒 Check SSL'
                        )}
                      </button>
                    )}

                    <button
                      className="delete-btn"
                      onClick={() => handleDeleteDomain(domain.id)}
                    >
                      🗑️
                    </button>
                  </div>
                </div>

                {!domain.isVerified && (
                  <div className="verification-section">
                    <h4>📋 DNS Setup Instructions</h4>
                    <p className="setup-intro">
                      To use <strong>{domain.domain}</strong> for short links, add these DNS records to your domain registrar:
                    </p>

                    <div className="dns-instructions">
                      <div className="dns-record">
                        <h5>🎯 Step 1: Main Domain Record</h5>
                        <div className="record-details">
                          <div className="record-field">
                            <label>Type:</label>
                            <code className="record-value">CNAME</code>
                          </div>
                          <div className="record-field">
                            <label>Name:</label>
                            <code className="record-value">@ (or leave blank for root domain)</code>
                          </div>
                          <div className="record-field">
                            <label>Value:</label>
                            <code className="record-value">djx3gluyy7g5i.amplifyapp.com</code>
                            <button
                              className="copy-record-btn"
                              onClick={() => navigator.clipboard.writeText('djx3gluyy7g5i.amplifyapp.com')}
                            >
                              📋
                            </button>
                          </div>
                          <div className="record-field">
                            <label>TTL:</label>
                            <code className="record-value">300 (or Auto)</code>
                          </div>
                        </div>
                      </div>

                      <div className="dns-record">
                        <h5>🌐 Step 2: WWW Subdomain (Optional)</h5>
                        <div className="record-details">
                          <div className="record-field">
                            <label>Type:</label>
                            <code className="record-value">CNAME</code>
                          </div>
                          <div className="record-field">
                            <label>Name:</label>
                            <code className="record-value">www</code>
                          </div>
                          <div className="record-field">
                            <label>Value:</label>
                            <code className="record-value">djx3gluyy7g5i.amplifyapp.com</code>
                            <button
                              className="copy-record-btn"
                              onClick={() => navigator.clipboard.writeText('djx3gluyy7g5i.amplifyapp.com')}
                            >
                              📋
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="setup-help">
                      <h5>📚 Common DNS Providers:</h5>
                      <div className="provider-list">
                        <div className="provider-item">
                          <strong>Cloudflare:</strong> DNS → Records → Add Record
                        </div>
                        <div className="provider-item">
                          <strong>GoDaddy:</strong> DNS Management → Add Record
                        </div>
                        <div className="provider-item">
                          <strong>Namecheap:</strong> Advanced DNS → Add New Record
                        </div>
                        <div className="provider-item">
                          <strong>Google Domains:</strong> DNS → Custom Records
                        </div>
                      </div>
                    </div>

                    <div className="verification-status">
                      <div className="status-info">
                        <h5>⏱️ Verification Process:</h5>
                        <ol className="verification-steps">
                          <li>Add the DNS records above to your domain registrar</li>
                          <li>Wait 5-15 minutes for DNS propagation</li>
                          <li>Click "🔍 Verify" button below</li>
                          <li>Once verified, your domain will be ready for short links!</li>
                        </ol>
                      </div>

                      <div className="test-tools">
                        <h5>🧪 Test DNS Propagation:</h5>
                        <p>Check if your DNS changes have propagated:</p>
                        <div className="test-links">
                          <a
                            href={`https://dnschecker.org/#CNAME/${domain.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="test-link"
                          >
                            🔍 Check DNS Propagation
                          </a>
                          <a
                            href={`https://www.whatsmydns.net/#CNAME/${domain.domain}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="test-link"
                          >
                            🌐 Global DNS Check
                          </a>
                        </div>
                      </div>
                    </div>

                    <div className="ssl-info">
                      <h5>🔒 SSL Certificate (HTTPS)</h5>
                      <div className="ssl-details">
                        <div className="ssl-option">
                          <h6>🤖 Automatic SSL (Recommended)</h6>
                          <p>AWS Amplify will automatically provision a free SSL certificate when you:</p>
                          <ol className="ssl-steps">
                            <li>Add your domain to AWS Amplify Console</li>
                            <li>Use the DNS records provided by Amplify (instead of the ones above)</li>
                            <li>Amplify handles SSL certificate provisioning automatically</li>
                          </ol>
                          <p><strong>Benefits:</strong> Free, automatic renewal, managed by AWS</p>
                        </div>

                        <div className="ssl-option">
                          <h6>⚙️ Manual SSL Setup</h6>
                          <p>If you prefer to manage SSL yourself:</p>
                          <ol className="ssl-steps">
                            <li>Use the DNS records above to point to our app</li>
                            <li>Configure SSL in your domain provider (CloudFlare, etc.)</li>
                            <li>Ensure SSL redirects to our Amplify endpoint</li>
                          </ol>
                          <p><strong>Note:</strong> Requires SSL proxy or CDN configuration</p>
                        </div>
                      </div>
                    </div>

                    <div className="verification-note">
                      <p><strong>💡 Pro Tip:</strong> For the easiest setup with automatic SSL, add your domain directly in AWS Amplify Console and use their provided DNS records instead.</p>
                    </div>
                  </div>
                )}

                {/* Default Destination URL Setting */}
                <div className="default-destination">
                  <h4>🎯 Default Destination</h4>
                  <p>Where to redirect when no short code is found</p>
                  <div className="destination-input">
                    <input
                      type="url"
                      value={domain.defaultDestinationUrl || ''}
                      onChange={(e) => handleUpdateDefaultDestination(domain.id, e.target.value)}
                      placeholder="https://trialconnx.com"
                      className="destination-url-input"
                    />
                    <span className="destination-help">
                      {domain.defaultDestinationUrl ?
                        `${domain.domain} → ${domain.defaultDestinationUrl}` :
                        `${domain.domain} → Fun 404 page`
                      }
                    </span>
                  </div>
                </div>

                <div className="domain-meta">
                  <span className="created-date">
                    Added {new Date(domain.createdAt).toLocaleDateString()}
                  </span>
                  {domain.isVerified && (
                    <span className="verified-date">
                      Verified {new Date(domain.updatedAt).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">🌐</div>
            <h3>No custom domains yet</h3>
            <p>Add your first custom domain to create branded short links</p>
            <button
              className="add-first-btn"
              onClick={() => setShowAddModal(true)}
            >
              Add Your First Domain
            </button>
          </div>
        )}
      </div>

      {/* Add Domain Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="add-domain-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>➕ Add Custom Domain</h3>
              <button
                className="close-btn"
                onClick={() => setShowAddModal(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleAddDomain} className="domain-form">
              <div className="form-group">
                <label className="form-label">Domain Name</label>
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  className="form-input"
                  placeholder="example.com"
                  required
                  autoFocus
                />
                <span className="form-hint">
                  Enter your domain without "https://" or "www"
                </span>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={() => setShowAddModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="submit-btn">
                  Add Domain
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .modern-domains {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
        }

        .domains-hero {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 20px;
          padding: 32px;
          margin-bottom: 32px;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .domains-hero::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px);
          background-size: 30px 30px;
          animation: float 20s linear infinite;
        }

        @keyframes float {
          0% { transform: translate(0, 0); }
          100% { transform: translate(-30px, -30px); }
        }

        .hero-content {
          position: relative;
          z-index: 1;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .title-section h1 {
          margin: 0 0 8px 0;
          font-size: 32px;
          font-weight: 700;
        }

        .title-section p {
          margin: 0;
          font-size: 16px;
          opacity: 0.9;
        }

        .add-domain-btn {
          background: rgba(255, 255, 255, 0.2);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          padding: 12px 20px;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
          backdrop-filter: blur(10px);
        }

        .add-domain-btn:hover {
          background: rgba(255, 255, 255, 0.3);
          transform: translateY(-2px);
        }

        .domains-section {
          margin-bottom: 32px;
        }

        .loading-state,
        .empty-state {
          text-align: center;
          padding: 80px 20px;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
        }

        .spinner {
          width: 48px;
          height: 48px;
          border: 4px solid #f3f4f6;
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 20px;
        }

        .empty-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }

        .empty-state h3 {
          margin: 0 0 12px 0;
          font-size: 24px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .empty-state p {
          margin: 0 0 24px 0;
          color: #6b7280;
          font-size: 16px;
        }

        .add-first-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border: none;
          padding: 14px 24px;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .add-first-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.3);
        }

        .domains-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
          gap: 24px;
        }

        .domain-card {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 24px;
          transition: all 0.3s ease;
        }

        .domain-card:hover {
          border-color: #d1d5db;
          box-shadow: 0 8px 24px rgba(0,0,0,0.08);
          transform: translateY(-2px);
        }

        .domain-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 16px;
        }

        .domain-name {
          margin: 0 0 8px 0;
          font-size: 20px;
          color: #1a1a1a;
          font-weight: 600;
          font-family: 'Monaco', 'Menlo', monospace;
        }

        .domain-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .status-badge,
        .type-badge {
          font-size: 12px;
          padding: 4px 10px;
          border-radius: 8px;
          font-weight: 500;
        }

        .status-badge.verified {
          background: #d1fae5;
          color: #065f46;
        }

        .status-badge.pending {
          background: #fef3c7;
          color: #92400e;
        }

        .type-badge {
          background: #e0e7ff;
          color: #3730a3;
        }

        .ssl-badge {
          font-size: 12px;
          padding: 4px 10px;
          border-radius: 8px;
          font-weight: 500;
        }

        .ssl-badge.issued {
          background: #d1fae5;
          color: #065f46;
        }

        .ssl-badge.pending {
          background: #fef3c7;
          color: #92400e;
        }

        .ssl-badge.failed {
          background: #fee2e2;
          color: #991b1b;
        }

        .ssl-badge.processing {
          background: #dbeafe;
          color: #1d4ed8;
        }

        .domain-actions {
          display: flex;
          gap: 8px;
        }

        .verify-btn,
        .delete-btn {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          padding: 8px 12px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .verify-btn:hover {
          background: #f0f9ff;
          border-color: #93c5fd;
          color: #1d4ed8;
        }

        .ssl-check-btn {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          padding: 8px 12px;
          border-radius: 8px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .ssl-check-btn:hover {
          background: #f0fdf4;
          border-color: #a7f3d0;
          color: #059669;
        }

        .delete-btn:hover {
          background: #fef2f2;
          border-color: #fca5a5;
          color: #dc2626;
        }

        .btn-spinner {
          width: 12px;
          height: 12px;
          border: 2px solid #f3f4f6;
          border-top: 2px solid currentColor;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-right: 6px;
        }

        .verification-section {
          background: #f9fafb;
          border-radius: 12px;
          padding: 20px;
          margin: 16px 0;
          border: 1px solid #e5e7eb;
        }

        .verification-section h4 {
          margin: 0 0 12px 0;
          font-size: 16px;
          color: #374151;
          font-weight: 600;
        }

        .setup-intro {
          margin: 0 0 20px 0;
          font-size: 14px;
          color: #6b7280;
          line-height: 1.5;
        }

        .dns-instructions {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 20px;
        }

        .dns-record {
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 16px;
        }

        .dns-record h5 {
          margin: 0 0 12px 0;
          font-size: 14px;
          color: #1f2937;
          font-weight: 600;
        }

        .record-details {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .record-field {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .record-field label {
          min-width: 60px;
          font-size: 13px;
          color: #6b7280;
          font-weight: 500;
        }

        .record-value {
          flex: 1;
          background: #f3f4f6;
          padding: 6px 10px;
          border-radius: 4px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 12px;
          color: #1f2937;
          border: 1px solid #d1d5db;
        }

        .copy-record-btn {
          background: #667eea;
          color: white;
          border: none;
          padding: 4px 8px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          transition: all 0.2s ease;
        }

        .copy-record-btn:hover {
          background: #5a6fd8;
          transform: scale(1.05);
        }

        .setup-help {
          background: #fffbeb;
          border: 1px solid #fbbf24;
          border-radius: 8px;
          padding: 12px;
          margin: 16px 0;
        }

        .setup-help h5 {
          margin: 0 0 8px 0;
          font-size: 13px;
          color: #92400e;
          font-weight: 600;
        }

        .provider-list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .provider-item {
          font-size: 12px;
          color: #92400e;
          line-height: 1.4;
        }

        .verification-status {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-top: 16px;
        }

        .status-info {
          background: white;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 12px;
        }

        .status-info h5 {
          margin: 0 0 8px 0;
          font-size: 13px;
          color: #374151;
          font-weight: 600;
        }

        .verification-steps {
          margin: 0;
          padding-left: 16px;
          font-size: 12px;
          color: #6b7280;
          line-height: 1.4;
        }

        .verification-steps li {
          margin-bottom: 4px;
        }

        .test-tools {
          background: #f0f9ff;
          border: 1px solid #3b82f6;
          border-radius: 8px;
          padding: 12px;
        }

        .test-tools h5 {
          margin: 0 0 6px 0;
          font-size: 13px;
          color: #1d4ed8;
          font-weight: 600;
        }

        .test-tools p {
          margin: 0 0 8px 0;
          font-size: 11px;
          color: #1e40af;
        }

        .test-links {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .test-link {
          background: #1d4ed8;
          color: white;
          padding: 6px 10px;
          border-radius: 4px;
          text-decoration: none;
          font-size: 11px;
          font-weight: 500;
          text-align: center;
          transition: all 0.2s ease;
        }

        .test-link:hover {
          background: #1e40af;
          transform: translateY(-1px);
        }

        .verification-note {
          background: #ecfdf5;
          border: 1px solid #10b981;
          border-radius: 8px;
          padding: 12px;
          margin-top: 16px;
        }

        .verification-note p {
          margin: 0;
          font-size: 12px;
          color: #059669;
          line-height: 1.4;
        }

        .ssl-info {
          background: #f0f9ff;
          border: 1px solid #3b82f6;
          border-radius: 8px;
          padding: 16px;
          margin: 16px 0;
        }

        .ssl-info h5 {
          margin: 0 0 12px 0;
          font-size: 14px;
          color: #1d4ed8;
          font-weight: 600;
        }

        .ssl-details {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .ssl-option {
          background: white;
          border: 1px solid #bfdbfe;
          border-radius: 6px;
          padding: 12px;
        }

        .ssl-option h6 {
          margin: 0 0 8px 0;
          font-size: 13px;
          color: #1e40af;
          font-weight: 600;
        }

        .ssl-option p {
          margin: 0 0 8px 0;
          font-size: 12px;
          color: #1e40af;
          line-height: 1.4;
        }

        .ssl-steps {
          margin: 0 0 8px 0;
          padding-left: 16px;
          font-size: 11px;
          color: #3730a3;
          line-height: 1.3;
        }

        .ssl-steps li {
          margin-bottom: 4px;
        }

        .default-destination {
          background: #f9fafb;
          border-radius: 12px;
          padding: 16px;
          margin: 16px 0;
        }

        .default-destination h4 {
          margin: 0 0 4px 0;
          font-size: 14px;
          color: #374151;
          font-weight: 600;
        }

        .default-destination p {
          margin: 0 0 12px 0;
          font-size: 12px;
          color: #6b7280;
        }

        .destination-input {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .destination-url-input {
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          transition: all 0.2s ease;
        }

        .destination-url-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
        }

        .destination-help {
          font-size: 11px;
          color: #6b7280;
          font-family: 'Monaco', 'Menlo', monospace;
          background: white;
          padding: 6px 8px;
          border-radius: 4px;
          border: 1px solid #e5e7eb;
        }

        .domain-meta {
          display: flex;
          justify-content: between;
          gap: 16px;
          font-size: 12px;
          color: #6b7280;
          border-top: 1px solid #e5e7eb;
          padding-top: 16px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .add-domain-modal {
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
          max-width: 500px;
          width: 100%;
        }

        .modal-header {
          padding: 24px 24px 16px;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-header h3 {
          margin: 0;
          font-size: 20px;
          color: #1a1a1a;
          font-weight: 600;
        }

        .close-btn {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #6b7280;
          padding: 4px;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .close-btn:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .domain-form {
          padding: 24px;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }

        .form-input {
          width: 100%;
          padding: 12px 16px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          font-size: 14px;
          background: #f9fafb;
          transition: all 0.3s ease;
        }

        .form-input:focus {
          outline: none;
          border-color: #667eea;
          background: white;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .form-hint {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          color: #6b7280;
        }

        .modal-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
        }

        .cancel-btn,
        .submit-btn {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .cancel-btn {
          background: #f9fafb;
          color: #374151;
          border: 1px solid #d1d5db;
        }

        .cancel-btn:hover {
          background: #f3f4f6;
        }

        .submit-btn {
          background: #667eea;
          color: white;
          border: none;
        }

        .submit-btn:hover {
          background: #5a6fd8;
          transform: translateY(-1px);
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .hero-content {
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .domains-grid {
            grid-template-columns: 1fr;
          }

          .domain-header {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }

          .domain-actions {
            justify-content: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default ModernDomainsPage;